import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { logoBase64 } from '../../../../assets/logoBase64';

declare module 'jspdf' {
  interface jsPDF {
    autoTable: typeof autoTable;
    lastAutoTable: {
      finalY: number;
    };
  }
}

interface DetalleLinea {
  presentacion: string;
  calibre: string;
  kilos: number;
  precioMP: number;
  montoMP: number;
  precioServicio: number;
  montoServicio: number;
}

interface OrdenFacturacionPdfData {
  numero: string;
  fecha: string;
  numeroDocumento: string;
  cliente: string;
  numeroParte: number;
  responsable: string;
  mallas: number;
  cajas: number;
  tipoProceso: string;
  detalles: DetalleLinea[];
  totalKilos: number;
  subTotal: number;
  detraccion: number;
  totalFacturarMP: number;
  subTotalServicio: number;
  igv: number;
  totalServicioConIgv: number;
  detraccionCliente: number;
  cuotaUnica: number;
}

const formatCurrency = (n: number) =>
  `S/ ${n.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatNumber = (n: number) =>
  n.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatDate = (dateStr: string) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
};

export const ordenFacturacionPdfService = {
  generatePdf(data: OrdenFacturacionPdfData) {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    let yPos = margin;

    // ==========================================
    // HEADER
    // ==========================================
    const logoWidth = 25;
    const logoHeight = 17;
    doc.addImage(logoBase64, 'PNG', margin, yPos, logoWidth, logoHeight);

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('ORDEN DE FACTURACION', pageWidth / 2, yPos + 8, { align: 'center' });

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text('CPP. ISABEL GOMEZ DE VIZA', pageWidth - margin, yPos + 5, { align: 'right' });

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(`N° ${data.numero}`, pageWidth - margin, yPos + 13, { align: 'right' });

    yPos += 24;

    // ==========================================
    // INFORMACION GENERAL
    // ==========================================
    doc.setFontSize(8);
    const col1 = margin + 3;
    const col2 = pageWidth / 2 + 10;
    const labelWidth = 30;
    const lineHeight = 5;

    const drawField = (label: string, value: string, x: number, y: number) => {
      doc.setFont('helvetica', 'bold');
      doc.text(`${label}:`, x, y);
      doc.setFont('helvetica', 'normal');
      doc.text(value, x + labelWidth, y);
    };

    const infoStartY = yPos - 2;
    drawField('N° DERC', data.numeroDocumento || '-', col1, yPos);
    drawField('Fecha', formatDate(data.fecha), col2, yPos);
    yPos += lineHeight;

    drawField('Cliente', data.cliente || '-', col1, yPos);
    drawField('N° Parte', String(data.numeroParte || '-'), col2, yPos);
    yPos += lineHeight;

    drawField('Responsable', data.responsable || '-', col1, yPos);
    drawField('Mallas', String(data.mallas || 0), col2, yPos);
    yPos += lineHeight;

    drawField('Cajas', String(data.cajas || 0), col1, yPos);
    drawField('Proceso', data.tipoProceso || '-', col2, yPos);
    yPos += lineHeight;

    const infoEndY = yPos + 5;
    doc.setDrawColor(180);
    doc.rect(margin, infoStartY - 1, pageWidth - 2 * margin, infoEndY - infoStartY + 1);

    yPos = infoEndY + 4;

    // ==========================================
    // DETALLE TABLE
    // ==========================================
    const tableHeaders = [
      'Presentación',
      'Calibre',
      'Kilos',
      'Precio MP',
      'Monto MP',
      'Precio Serv.',
      'Monto Serv.',
    ];

    // Group by presentacion for subtotals
    const presentaciones = [...new Set(data.detalles.map((d) => d.presentacion))];
    const tableBody: (string | { content: string; styles: Record<string, unknown> })[][] = [];

    for (const pres of presentaciones) {
      const items = data.detalles.filter((d) => d.presentacion === pres);
      let presKilos = 0;
      let presMontoMP = 0;
      let presMontoServ = 0;

      for (const item of items) {
        presKilos += item.kilos;
        presMontoMP += item.montoMP;
        presMontoServ += item.montoServicio;

        tableBody.push([
          item === items[0] ? pres : '',
          item.calibre,
          formatNumber(item.kilos),
          formatCurrency(item.precioMP),
          formatCurrency(item.montoMP),
          formatCurrency(item.precioServicio),
          formatCurrency(item.montoServicio),
        ]);
      }

      // Subtotal row per presentacion
      tableBody.push([
        { content: `Subtotal ${pres}`, styles: { fontStyle: 'bold' as const, fillColor: [240, 240, 240] } },
        { content: '', styles: { fillColor: [240, 240, 240] } },
        { content: formatNumber(presKilos), styles: { fontStyle: 'bold' as const, fillColor: [240, 240, 240] } },
        { content: '', styles: { fillColor: [240, 240, 240] } },
        { content: formatCurrency(presMontoMP), styles: { fontStyle: 'bold' as const, fillColor: [240, 240, 240] } },
        { content: '', styles: { fillColor: [240, 240, 240] } },
        { content: formatCurrency(presMontoServ), styles: { fontStyle: 'bold' as const, fillColor: [240, 240, 240] } },
      ]);
    }

    autoTable(doc, {
      startY: yPos,
      head: [tableHeaders],
      body: tableBody,
      theme: 'grid',
      styles: { fontSize: 7, cellPadding: 1.5 },
      headStyles: {
        fillColor: [50, 50, 50],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        halign: 'center',
      },
      columnStyles: {
        0: { cellWidth: 35 },
        1: { cellWidth: 20 },
        2: { halign: 'right', cellWidth: 18 },
        3: { halign: 'right', cellWidth: 22 },
        4: { halign: 'right', cellWidth: 25 },
        5: { halign: 'right', cellWidth: 22 },
        6: { halign: 'right', cellWidth: 25 },
      },
      margin: { left: margin, right: margin },
    });

    yPos = doc.lastAutoTable.finalY + 8;

    // ==========================================
    // RESUMEN DE FACTURACION
    // ==========================================
    const summaryX = pageWidth / 2 + 5;
    const summaryValueX = pageWidth - margin;

    const drawSummaryLine = (label: string, value: string, y: number, opts?: { bold?: boolean; color?: number[] }) => {
      doc.setFont('helvetica', opts?.bold ? 'bold' : 'normal');
      if (opts?.color) {
        doc.setTextColor(opts.color[0], opts.color[1], opts.color[2]);
      } else {
        doc.setTextColor(0, 0, 0);
      }
      doc.text(label, summaryX, y);
      doc.text(value, summaryValueX, y, { align: 'right' });
      doc.setTextColor(0, 0, 0);
    };

    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text('Materia Prima', summaryX, yPos);
    yPos += lineHeight;

    drawSummaryLine('SubTotal (MP):', formatCurrency(data.subTotal), yPos);
    yPos += lineHeight;

    drawSummaryLine('Detracción (1.5%):', `- ${formatCurrency(data.detraccion)}`, yPos, { color: [200, 0, 0] });
    yPos += lineHeight;

    drawSummaryLine('Total Facturar (MP):', formatCurrency(data.totalFacturarMP), yPos, { bold: true });
    yPos += lineHeight + 2;

    // Separator
    doc.setDrawColor(180);
    doc.line(summaryX, yPos - 1, summaryValueX, yPos - 1);
    yPos += 2;

    doc.setFont('helvetica', 'bold');
    doc.text('Servicio', summaryX, yPos);
    yPos += lineHeight;

    drawSummaryLine('SubTotal Servicio:', formatCurrency(data.subTotalServicio), yPos);
    yPos += lineHeight;

    drawSummaryLine('IGV (18%):', formatCurrency(data.igv), yPos);
    yPos += lineHeight;

    drawSummaryLine('Total Serv. con IGV:', formatCurrency(data.totalServicioConIgv), yPos, { bold: true });
    yPos += lineHeight;

    drawSummaryLine('Detracción Cliente (12%):', `- ${formatCurrency(data.detraccionCliente)}`, yPos, { color: [200, 0, 0] });
    yPos += lineHeight + 2;

    // Final separator
    doc.setDrawColor(0);
    doc.line(summaryX, yPos - 1, summaryValueX, yPos - 1);
    yPos += 3;

    doc.setFontSize(10);
    drawSummaryLine('Cuota Única:', formatCurrency(data.cuotaUnica), yPos, { bold: true });

    // ==========================================
    // SAVE / OPEN
    // ==========================================
    doc.save(`Orden_Facturacion_${data.numero}.pdf`);
  },
};
